import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

function groupStorageKey(groupId: string): string {
  const prefix = "quartel-admin-sidebar-group:";
  return `${prefix}${groupId}`;
}

function scrollStorageKey(scope: string): string {
  const prefix = "quartel-admin-sidebar-scroll:";
  return `${prefix}${scope}`;
}

function notebookStorageKey(): string {
  const key = "quartel-admin-sidebar-notebook-expanded";
  return key;
}

function readStoredBoolean(key: string, fallback: boolean): boolean {
  try {
    const stored = globalThis.localStorage.getItem(key);
    if (stored === "true") return true;
    if (stored === "false") return false;
    return fallback;
  } catch {
    return fallback;
  }
}

function writeStoredValue(key: string, value: string): void {
  try {
    globalThis.localStorage.setItem(key, value);
  } catch {
    // Persistence is a convenience; private browsing must not break navigation.
  }
}

export function useSidebarGroupState(
  groupId: string,
  initiallyOpen: boolean,
): readonly [boolean, (open: boolean) => void] {
  const key = groupStorageKey(groupId);
  const [open, setOpen] = useState(() => readStoredBoolean(key, initiallyOpen));
  const update = useCallback(
    (next: boolean) => {
      setOpen(next);
      writeStoredValue(key, String(next));
    },
    [key],
  );
  return [open, update] as const;
}

type SidebarAccordionListener = (openedGroupId: string) => void;

// Module-level so the desktop rail and the mobile drawer (separate React
// trees) stay in accordion lock-step, mirroring the shared storage keys.
const sidebarAccordionListeners = new Set<SidebarAccordionListener>();

function notifySidebarGroupOpened(openedGroupId: string): void {
  for (const listener of [...sidebarAccordionListeners]) {
    listener(openedGroupId);
  }
}

function subscribeSiblingCollapse(
  groupId: string,
  hasActiveChildRef: React.RefObject<boolean>,
  persistClosed: (open: boolean) => void,
): () => void {
  const collapseWhenSiblingOpens: SidebarAccordionListener = (openedId) => {
    if (openedId === groupId || hasActiveChildRef.current) return;
    persistClosed(false);
  };
  sidebarAccordionListeners.add(collapseWhenSiblingOpens);
  return () => {
    sidebarAccordionListeners.delete(collapseWhenSiblingOpens);
  };
}

// #232 sidebar addendum (03/08): a single expanded group must fit common
// viewport heights without an internal scrollbar, so opening one group
// collapses its sibling. The group owning the active route never collapses
// (reachability contract), which `persistSidebarGroupToggle` also enforces.
export function useSidebarAccordionGroup(
  groupId: string,
  initiallyOpen: boolean,
  hasActiveChild: boolean,
): readonly [boolean, (open: boolean) => void] {
  const [open, setOpen] = useSidebarGroupState(groupId, initiallyOpen);
  const hasActiveChildRef = useRef(hasActiveChild);
  hasActiveChildRef.current = hasActiveChild;
  useEffect(
    () => subscribeSiblingCollapse(groupId, hasActiveChildRef, setOpen),
    [groupId, setOpen],
  );
  const openExclusive = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) notifySidebarGroupOpened(groupId);
    },
    [groupId, setOpen],
  );
  return [open, openExclusive] as const;
}

export function useSidebarNotebookExpansion(): readonly [boolean, () => void] {
  const [expanded, setExpanded] = useState(() =>
    readStoredBoolean(notebookStorageKey(), false),
  );
  const toggle = useRef(() =>
    setExpanded((current) => {
      const next = !current;
      writeStoredValue(notebookStorageKey(), String(next));
      return next;
    }),
  ).current;
  return [expanded, toggle] as const;
}

export function useSidebarScrollPersistence(scope: string): {
  readonly navRef: React.RefObject<HTMLElement | null>;
  readonly rememberScroll: (event: React.UIEvent<HTMLElement>) => void;
} {
  const key = scrollStorageKey(scope);
  const navRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => restoreScroll(navRef.current, key), [key]);
  const rememberScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) =>
      writeStoredValue(key, String(event.currentTarget.scrollTop)),
    [key],
  );
  return { navRef, rememberScroll };
}

function restoreScroll(element: HTMLElement | null, key: string): void {
  if (!element) return;
  const stored = readStoredNumber(key);
  if (stored !== null) element.scrollTop = stored;
}

function readStoredNumber(key: string): number | null {
  try {
    const stored = Number(globalThis.localStorage.getItem(key));
    return Number.isFinite(stored) && stored > 0 ? stored : null;
  } catch {
    return null;
  }
}

export function persistSidebarGroupToggle(
  event: React.SyntheticEvent<HTMLDetailsElement>,
  activeChild: boolean,
  persist: (open: boolean) => void,
): void {
  if (activeChild && !event.currentTarget.open) {
    event.currentTarget.open = true;
    return;
  }
  persist(event.currentTarget.open);
}
